// Pure helpers for editor signature help: figure out which function/procedure
// call the cursor is inside, and which argument it's on. No DOM — node-testable
// (same ethos as lib/qbsql.js / lib/sqlformat.js). The CodeMirror layer turns
// the result into a tooltip; the actual signatures come from the built-in
// FUNCTIONS catalogue and the live routine list.

// Replace string / backtick / comment bodies with spaces so a backward paren
// scan can't be fooled by a '(' or ',' inside a literal or comment. Length is
// preserved so indices still line up with the original text.
function maskLiterals(s) {
  const a = s.split('');
  let inS = false, inD = false, inB = false, inLine = false, inBlock = false;
  for (let i = 0; i < a.length; i++) {
    const c = a[i], n = a[i + 1];
    if (inLine) { if (c === '\n') inLine = false; else a[i] = ' '; continue; }
    if (inBlock) { if (c === '*' && n === '/') { a[i] = ' '; a[i + 1] = ' '; i++; inBlock = false; } else a[i] = ' '; continue; }
    if (inS) { a[i] = ' '; if (c === '\\') { a[i + 1] = ' '; i++; } else if (c === "'") inS = false; continue; }
    if (inD) { a[i] = ' '; if (c === '\\') { a[i + 1] = ' '; i++; } else if (c === '"') inD = false; continue; }
    if (inB) { a[i] = ' '; if (c === '`') inB = false; continue; }
    if (c === '-' && n === '-') { inLine = true; a[i] = ' '; a[i + 1] = ' '; i++; continue; }
    if (c === '#') { inLine = true; a[i] = ' '; continue; }
    if (c === '/' && n === '*') { inBlock = true; a[i] = ' '; a[i + 1] = ' '; i++; continue; }
    if (c === "'") { inS = true; a[i] = ' '; continue; }
    if (c === '"') { inD = true; a[i] = ' '; continue; }
    if (c === '`') { inB = true; a[i] = ' '; continue; }
  }
  return a.join('');
}

// callContextAt(textBefore): given the text from (at least) the start of the
// current statement up to the cursor, return { name, argIndex } for the
// innermost function/procedure call the cursor sits inside, or null if the
// cursor isn't inside a call's argument list. argIndex is 0-based.
export function callContextAt(textBefore) {
  if (!textBefore) return null;
  const s = maskLiterals(textBefore);
  let depth = 0, commas = 0, open = -1;
  for (let i = s.length - 1; i >= 0; i--) {
    const c = s[i];
    if (c === ')') depth++;
    else if (c === '(') {
      if (depth === 0) { open = i; break; }
      depth--;
    } else if (c === ',' && depth === 0) commas++;
  }
  if (open < 0) return null; // not inside parentheses
  // The '(' must sit IMMEDIATELY after an identifier — `name(`. A space before it
  // (`SELECT (a + b)`) means a grouping paren, not a call, so we bail. This is the
  // conservative choice; an unknown name is filtered later by the signature lookup.
  let j = open - 1;
  if (j < 0 || !/[A-Za-z0-9_$]/.test(s[j])) return null;
  const end = j + 1;
  while (j >= 0 && /[A-Za-z0-9_$]/.test(s[j])) j--;
  const name = textBefore.slice(j + 1, end); // identifiers survive masking unchanged
  if (!name || !/^[A-Za-z_$][\w$]*$/.test(name)) return null;
  return { name, argIndex: commas };
}

// splitSigArgs('DATE_FORMAT(date, format)') → { name:'DATE_FORMAT',
// args:['date','format'], trailing:'' }. Splits only top-level commas, and
// captures any text after the closing ')' (e.g. ' RETURNS int' / ' → int').
export function splitSigArgs(sig) {
  const op = sig.indexOf('(');
  const cp = sig.lastIndexOf(')');
  if (op < 0 || cp <= op) return { name: sig.trim(), args: [], trailing: '' };
  const name = sig.slice(0, op).trim();
  const inner = sig.slice(op + 1, cp).trim();
  const trailing = sig.slice(cp + 1);
  if (!inner) return { name, args: [], trailing };
  const args = [];
  let depth = 0, cur = '';
  for (const ch of inner) {
    if (ch === '(') { depth++; cur += ch; }
    else if (ch === ')') { depth--; cur += ch; }
    else if (ch === ',' && depth === 0) { args.push(cur.trim()); cur = ''; }
    else cur += ch;
  }
  if (cur.trim()) args.push(cur.trim());
  return { name, args, trailing };
}
